import { Link } from 'react-router-dom';
import { usePageMeta } from '../../hooks/usePageMeta';
import { useI18nLocaleSync } from '../../hooks/useI18nLocaleSync';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';

export function KoreanLotto645() {
  const lp = useLocalizedPath();
  useI18nLocaleSync();
  usePageMeta({
    title: 'Korean Lotto 6/45 – Complete Guide to 동행복권 | Lotto Number Generator',
    description:
      'Everything about Korean Lotto 6/45 (로또 6/45): how to play, prize tiers, jackpot odds, the drawing process, where to buy tickets, and historical jackpots.',
    path: '/articles/korean-lotto-645',
  });

  return (
    <main className="content">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to={lp('/')}>Home</Link>
        <span className="sep">/</span>
        <Link to="/articles/">Articles</Link>
        <span className="sep">/</span>
        <span className="current">Korean Lotto 6/45 Guide</span>
      </nav>

      <article className="about-section">
        <h1>Korean Lotto 6/45: A Complete Guide to South Korea's National Lottery</h1>
        <p>
          Korean Lotto 6/45 (로또 6/45) is South Korea's national lottery game and one of the most
          popular forms of legal recreational gambling in the country. Since its launch in December
          2002, it has run without interruption every Saturday, accumulated a database of more than
          1,200 draws, and produced thousands of jackpot winners. This guide explains the game's
          rules, odds, prize structure, history, and the practical details of participation.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Background and History</h2>
        <p>
          The Korean government introduced Lotto 6/45 in December 2002 as part of a broader effort to
          channel gambling demand into a regulated, taxed, government-operated framework. Prior to
          2002, various informal and semi-formal lotteries had operated in Korea with inconsistent
          oversight. The national lotto was designed from the outset to be transparent, integrity-
          verified, and dedicated to public benefit.
        </p>
        <p>
          The game is operated by <strong>동행복권</strong> (Dong-Haeng Bokwon, the Korea Lottery
          Commission) under authorization from the Ministry of Economy and Finance. Revenue from
          ticket sales is distributed as follows: approximately 50% to prize pool, with the remainder
          supporting public works, small business development funds, and lottery administration. The
          Korea Lottery Commission publishes detailed annual revenue and expenditure reports.
        </p>
        <p>
          The format has remained unchanged since launch: 6 numbers drawn from 1–45, plus one bonus
          number. This consistency means the game's historical database is a clean, uniform dataset—
          one reason our <Link to={lp('/')}>number generator</Link> can compare generated numbers
          against more than 1,200 documented winning draws.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>How to Play</h2>
        <p>
          Participation in Lotto 6/45 is simple. Players select{' '}
          <strong>6 numbers from 1 to 45</strong>. During each Saturday drawing, the Korea Lottery
          Commission draws 6 main numbers plus 1 bonus number from a drum of 45 balls. The bonus
          number is used only for determining 2nd-prize winners (players who matched exactly 5 main
          numbers).
        </p>
        <p>
          Tickets cost <strong>₩1,000 each</strong> (approximately $0.75 USD as of 2024). Players
          can choose their own numbers on a playslip or request an auto-selection (자동 선택). Each
          ticket can contain up to 5 games, each with a separate set of 6 numbers.
        </p>
        <p>
          Drawings are held every <strong>Saturday at 8:35 p.m. KST</strong> and broadcast live on
          MBC television. The live broadcast consistently draws large audiences, particularly when
          jackpots have been rolling over for several weeks.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Prize Tiers and Odds</h2>
        <p>
          The total jackpot odds are <strong>1 in 8,145,060</strong>—calculated as C(45,6), the
          number of ways to choose 6 numbers from a pool of 45. This is substantially more favorable
          than Powerball (1 in 292 million) or Mega Millions (1 in 290 million), which is why Lotto
          6/45 prizes are correspondingly smaller in absolute terms.
        </p>
        <p>
          The prize pool is funded by <strong>50% of total ticket revenue</strong> for each draw week.
          This is divided among the prize tiers as follows:
        </p>
        <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.9, color: 'var(--text-secondary)' }}>
          <li>
            <strong>1st prize (1등):</strong> Match all 6 main numbers — 75% of prize pool, split
            equally among all 1st-prize winners. Odds: 1 in 8,145,060.
          </li>
          <li>
            <strong>2nd prize (2등):</strong> Match 5 main numbers + bonus number — 12.5% of prize
            pool, split among winners. Typically ₩55–₩70 million per winner. Odds: 1 in 1,357,510.
          </li>
          <li>
            <strong>3rd prize (3등):</strong> Match 5 main numbers (no bonus) — 12.5% of prize pool,
            split among winners. Typically ₩1.5–₩2 million per winner. Odds: 1 in 35,724.
          </li>
          <li>
            <strong>4th prize (4등):</strong> Match 4 main numbers — fixed ₩50,000 per winner.
            Odds: 1 in 733.
          </li>
          <li>
            <strong>5th prize (5등):</strong> Match 3 main numbers — fixed ₩5,000 per winner.
            Odds: 1 in 45.
          </li>
        </ul>
        <p style={{ marginTop: 12 }}>
          The 1st through 3rd prize pools are distributed among all winners of that tier in a given
          draw. If many players win 1st prize in the same week (which happens when jackpots are large
          and ticket sales surge), the individual 1st-prize payout decreases accordingly. The 4th and
          5th prizes are fixed amounts regardless of how many winners there are.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>The Drawing Process</h2>
        <p>
          Lotto 6/45 drawings are conducted using a certified ball machine under the supervision of
          the Korea Lottery Commission, independent auditors, and notaries public. The drawing is
          broadcast live on MBC with on-screen display of each ball as it is drawn.
        </p>
        <p>
          The process draws 6 main numbers, then a separate bonus number, all from the same pool of
          45 balls. The bonus number cannot duplicate any of the 6 main numbers, since the drum is
          not reset between draws. Results are posted on the official lottery website (dhlottery.co.kr)
          immediately after the broadcast, and ticket-checking terminals at authorized retailers update
          within minutes.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Where to Buy Tickets</h2>
        <p>
          Lotto 6/45 tickets are sold at:
        </p>
        <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.9, color: 'var(--text-secondary)' }}>
          <li>
            <strong>Authorized retail locations:</strong> Convenience stores, lotto shops, and
            designated lottery kiosks throughout South Korea. Many convenience store chains (GS25,
            CU, 7-Eleven Korea) sell lottery tickets.
          </li>
          <li>
            <strong>Online:</strong> Tickets can be purchased through the official Korea Lottery
            website (dhlottery.co.kr) after registering with a verified Korean ID and bank account.
            Online purchases are limited to ₩100,000 worth of tickets per week.
          </li>
          <li>
            <strong>Mobile app:</strong> The official Korea Lottery app provides mobile ticket
            purchasing with the same weekly limits as online purchases.
          </li>
        </ul>
        <p>
          Note that ticket purchases are restricted to South Korean residents with valid national
          identification. Foreign visitors cannot purchase tickets at retail, and the online/mobile
          systems require Korean ID verification.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Tax Treatment of Winnings</h2>
        <p>
          Korean lottery prizes are subject to withholding tax at the following rates:
        </p>
        <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.9, color: 'var(--text-secondary)' }}>
          <li>Prizes up to ₩200,000: Tax-exempt</li>
          <li>Prizes from ₩200,001 to ₩30,000,000: 22% withholding (20% income tax + 2% local tax)</li>
          <li>Prizes over ₩30,000,000: 33% withholding (30% income tax + 3% local tax)</li>
        </ul>
        <p>
          All 1st, 2nd, and 3rd prize winners fall in the highest bracket. Tax is withheld
          automatically at the point of prize payment; winners receive the net amount directly. Unlike
          US lotteries, there is no choice between annuity and lump-sum for Korean lottery prizes—all
          prizes are paid as a single lump sum, net of tax, within two months of the drawing.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Notable Jackpots</h2>
        <p>
          Because the prize pool is based on weekly ticket sales and split among winners, Lotto 6/45
          jackpot sizes vary significantly. The largest 1st prizes in the game's history have exceeded
          ₩40 billion (approximately $30 million USD) in weeks with unusually high sales and no
          winner for multiple consecutive draws.
        </p>
        <p>
          The game has produced several notable prize structures where a single drawing had no 1st
          prize winner, causing the 1st-prize pool to roll over into the following week's draw. Unlike
          US lotteries, there is a rollover cap: if no 1st prize is won for several consecutive
          draws, the accumulated 1st-prize pool cascades down to 2nd-prize winners, preventing
          indefinite jackpot growth.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Generating Korean Lotto Numbers</h2>
        <p>
          Our free{' '}
          <Link to={lp('/')}>Lotto 6/45 number generator</Link> produces random sets of 6 numbers
          from 1–45 using a statistically uniform algorithm. Every generated set is immediately
          compared against our database of 1,200+ real past Korean Lotto 6/45 winning draws—if your
          numbers exactly match a documented jackpot-winning combination, the draw round and date are
          displayed. This is a historical curiosity feature only; it does not affect the probability
          of any future draw.
        </p>
        <p>
          The Korean-language version of the site is available at <Link to="/ko">lotto-number-generator.com/ko</Link>,
          where the interface and explanations are fully localized for Korean users.
        </p>

        <div className="disclaimer" style={{ marginTop: 28 }}>
          <strong>면책 조항 / Disclaimer:</strong> This site is not affiliated with 동행복권 or the
          Korea Lottery Commission. For official rules and current results, visit dhlottery.co.kr.
          For entertainment only.
        </div>

        <p style={{ marginTop: 20 }}>
          <Link to="/articles/">← Back to all articles</Link>
          {' · '}
          <Link to={lp('/guide')}>Lottery Guide</Link>
          {' · '}
          <Link to="/articles/how-powerball-works/">How Powerball Works</Link>
        </p>
      </article>
    </main>
  );
}
